import React, { useCallback } from "react";
import { useTranslation } from "locales";
import { useVirtualPrinterFormQuery } from "graphql-sdk";
import type { GQVirtualPrinterSave } from "graphql-sdk";
import {
    Surface,
    InputControl,
    TextInput,
    Button,
    Loading,
    Picker,
    useThemedStyle,
} from "@venuepos/react-common";
import type {
    Form as FormComponent,
    StyleFunction,
} from "@venuepos/react-common";
import produce from "immer";

export function Form(props: {
    form: FormComponent<GQVirtualPrinterSave>;
    onSubmit: () => void;
    submitButton: [string, string];
}) {
    const [t] = useTranslation();
    const [{ values, errors }, { setValue, handleSubmit }] = props.form;
    const styles = useThemedStyle(styleFunc);
    const { data, loading } = useVirtualPrinterFormQuery();

    const handleOnChange = useCallback(
        (value: string) => {
            setValue("printerId", value);
        },
        [setValue]
    );

    const handleOnDepartmentChange = useCallback(
        (printerId: string, departmentId: string) => {
            setValue(
                "departments",
                produce(values.departments, draft => {
                    const dep = draft.find(
                        itr => itr.departmentId === departmentId
                    );
                    if (dep) {
                        dep.printerId = printerId;
                    } else {
                        draft.push({
                            departmentId: departmentId,
                            printerId: printerId,
                        });
                    }
                })
            );
        },
        [setValue, values]
    );

    if (!values || !data) {
        return null;
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <Surface style={styles.surfaceContainer}>
                <InputControl error={errors.name}>
                    <TextInput
                        label={t("common.name", "Name")}
                        placeholder={t(
                            "backoffice.common.enter_name",
                            "Enter name"
                        )}
                        defaultValue={values.name || ""}
                        onChangeText={text => setValue("name", text)}
                    />
                </InputControl>

                <InputControl
                    error={errors.printerId}
                    description={t(
                        "backoffice.virtual_printer.printer_description",
                        "This printer will be used except if another printer has been choosen for a department"
                    )}
                >
                    <Picker
                        label={t(
                            "backoffice.virtual_printer.printer",
                            "Printer"
                        )}
                        onValueChange={handleOnChange}
                        selectedValue={values.printerId}
                    >
                        <Picker.Item
                            label={t(
                                "backoffice.virtual_printer.select_printer",
                                "Select printer"
                            )}
                            value=""
                        />
                        {data.printers.data
                            .filter(
                                itr =>
                                    itr.type === "IP" && itr.class === "Generic"
                            )
                            .map(item => (
                                <Picker.Item
                                    key={`${item.name} (${item.manufacturer})`}
                                    label={`${item.name} (${item.manufacturer})`}
                                    value={item.id}
                                />
                            ))}
                    </Picker>
                </InputControl>
            </Surface>
            <Surface style={styles.surfaceContainer}>
                {data.departments.data.map(department => (
                    <InputControl
                        description={t(
                            "backoffice.virtual_printer.printer_department_description",
                            "This printer will be used for the department"
                        )}
                        key={department.id}
                    >
                        <Picker
                            label={department.name}
                            onValueChange={val => {
                                return handleOnDepartmentChange(
                                    val,
                                    department.id
                                );
                            }}
                            selectedValue={
                                values.departments.find(
                                    itr => itr.departmentId === department.id
                                ) === undefined
                                    ? "DEFAULT"
                                    : values.departments.find(
                                          itr =>
                                              itr.departmentId === department.id
                                      )?.printerId
                            }
                        >
                            <Picker.Item
                                label={t(
                                    "backoffice.virtual_printer.use_default_printer",
                                    "Use default printer"
                                )}
                                value="DEFAULT"
                            />
                            <Picker.Item
                                label={t(
                                    "backoffice.virtual_printer.no_printer",
                                    "No printer"
                                )}
                                value=""
                            />
                            {data.printers.data
                                .filter(
                                    itr =>
                                        itr.type === "IP" &&
                                        itr.class === "Generic"
                                )
                                .map(item => (
                                    <Picker.Item
                                        key={`${item.name} (${item.manufacturer})`}
                                        label={`${item.name} (${item.manufacturer})`}
                                        value={item.id}
                                    />
                                ))}
                        </Picker>
                    </InputControl>
                ))}

                <Button onPress={handleSubmit(props.onSubmit)}>
                    {t(props.submitButton[0], props.submitButton[1])}
                </Button>
            </Surface>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    surfaceContainer: {
        marginVertical: theme.spacingScale / 2,
    },
});
